import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import Cookies from 'js-cookie'
import AppConfig from '@/config'
import { getUsage } from '@/api/endpoints/usage'
import { postCancelSubscription } from '@/api/endpoints/billing'
import { ApiError } from '@/api/errors'
import { BILLING_ERROR_CODES } from '@/api/types'
import { COOKIE_KEYS, getAuthToken } from '@/constants/storageKeys'
import { PLURAL_FORMS, type PluralForms } from '@/utils/pluralizeRu'

// Гостю до авторизации даём 3 бесплатных текстовых запроса.
export const GUEST_FREE_LIMIT = 3

const GUEST_FREE_TTL_DAYS = 365

/**
 * Статусы подписки. Перечисление на стороне бэка открытое (см. UsageResponse.subscription),
 * но эти четыре значения фронт называет по имени — здесь, чтобы литералы не расползлись
 * по стору, гейту и тестам тремя разными написаниями.
 */
export const SUBSCRIPTION_STATUS = {
	active: 'active',
	/** Автопродление отключено, но доступ живёт до current_period_end. */
	cancelled: 'cancelled',
	none: 'none',
	/** Автопродление сорвалось: Pro-доступ завершён, генерации закрыты до новой оплаты. */
	suspended: 'suspended'
} as const

// Контракт GET /usage (POMOGAI_FLOW_PLAN.md §5.2). Всё, кроме limits/period_end,
// опционально: бэк может выкатывать поля по частям.
export interface UsageLimit {
	limit: number
	used: number
	remaining: number
}

/** Ресурсы, которые тратятся по одной единице за действие. */
export type UsageResource = 'text' | 'image' | 'song'

export interface UsageResponse {
	plan?: string
	limits: Record<UsageResource, UsageLimit>
	period_end: string | null
	features?: { emotional_support?: boolean }
	subscription?: {
		id?: string
		/**
		 * Свободная строка от бэка: перечисление не фиксировано и может пополняться.
		 * Одно значение фронт читает — 'suspended' (см. isSuspended): сорванное продление
		 * запирает генерации. Остальные на решения не влияют, тариф определяет plan.
		 * Сравнение строгое: незнакомый статус значит «не suspended», то есть не запираем.
		 */
		status?: string
		current_period_end?: string | null
		/** Включено ли автопродление. */
		is_renewable?: boolean
		/**
		 * Последние 4 цифры карты числом, а не строкой: 42 означает карту, кончающуюся
		 * на 0042. Дополнять нулями обязан тот, кто показывает (см. SubscriptionCard).
		 */
		card_number?: number | null
	} | null
	storage_expires_days?: number
	/**
	 * Заказ, по которому уже была попытка списания (только статус processing).
	 * Нужен, чтобы после перезагрузки страницы фронт узнал про ожидание оплаты,
	 * даже если localStorage чист — например, человек вернулся из другого браузера.
	 */
	pending_order?: { order_id: string; expires_at?: string } | null
}

/**
 * Сырой вход: форма контракта пока плавает. Конверт data необязателен, а сам срез
 * лежит либо верхним уровнем (GET /usage), либо под ключом usage (событие стрима).
 */
export type RawUsage = Partial<UsageResponse> & {
	data?: RawUsage
	usage?: Partial<UsageResponse>
}

export interface UsageItem {
	key: 'text' | 'image' | 'song' | 'day'
	value: number
	forms: PluralForms
}

function readGuestUsed(): number {
	const raw = Number(Cookies.get(COOKIE_KEYS.GUEST_FREE_USED))
	if (!Number.isFinite(raw) || raw < 0) return 0
	return Math.min(GUEST_FREE_LIMIT, Math.floor(raw))
}

function writeGuestUsed(used: number): void {
	// Параметры те же, что у guest_id (см. utils/guestId.ts): счётчик живёт вместе с гостевой cookie.
	Cookies.set(COOKIE_KEYS.GUEST_FREE_USED, String(used), {
		expires: GUEST_FREE_TTL_DAYS,
		path: '/',
		sameSite: 'Lax',
		secure: window.location.protocol === 'https:'
	})
}

/**
 * Приводит ответ бэка к внутренней модели: снимаем необязательный конверт data и ищем
 * полный срез на двух уровнях — верхнем (GET /usage) и под ключом usage (событие стрима).
 *
 * Плоскую форму { usage: { remaining_requests } } фронт намеренно не читает. Лимита в ней
 * нет, и раскладывать её приходилось как limit = remaining — из-за этого правая цифра
 * счётчика съезжала вслед за остатком («1 из 1» вместо «2 из 3»), а срез Pro на каждом
 * текстовом стриме терял image/song, period_end и подписку. Неполному срезу лучше не
 * верить вовсе: тогда сработает фолбэк вызывающего, а не подмена данных догадкой.
 *
 * null — форму не распознали, состояние трогать нельзя.
 */
function normalizeUsage(raw: RawUsage | null | undefined): UsageResponse | null {
	const body = raw?.data ?? raw
	if (!body) return null
	if (body.limits?.text) return body as UsageResponse
	if (body.usage?.limits?.text) return body.usage as UsageResponse
	return null
}

function daysLeft(periodEnd: string | null): number {
	if (!periodEnd) return 0
	const diff = new Date(periodEnd).getTime() - Date.now()
	if (!Number.isFinite(diff) || diff <= 0) return 0
	return Math.ceil(diff / 86400000)
}

export const useUsageStore = defineStore('usage', () => {
	const serverUsage = ref<UsageResponse | null>(null)
	const guestUsed = ref(readGuestUsed())

	/**
	 * Гость: internal-режим без токена. Реактивных зависимостей у computed нет
	 * (authMode — обычное поле конфига, getAuthToken — чтение localStorage), поэтому внутри
	 * сессии приложения он не переключается. Это допустимо только потому, что обе смены
	 * авторизации проходят через полную загрузку страницы: вход — LoginCodeView, выход —
	 * authStore.logout(). Сменишь любую из них на SPA-навигацию — сначала сделай isGuest
	 * реактивным, иначе гостевая ветка items не включится.
	 */
	const isGuest = computed(() => AppConfig.authMode === 'internal' && !getAuthToken())
	const guestRemaining = computed(() => Math.max(0, GUEST_FREE_LIMIT - guestUsed.value))

	/** Тариф по данным /usage. Пустая строка — среза ещё нет (см. profile.plan как дубль). */
	const plan = computed(() => serverUsage.value?.plan ?? '')

	const isPro = computed(() => plan.value === 'pro')

	/**
	 * Срез для личного кабинета. Отдельные геттеры вместо чтения serverUsage напрямую:
	 * карточки профиля не должны знать про форму ответа и про то, что среза может не быть.
	 */
	const limits = computed(() => serverUsage.value?.limits ?? null)
	const periodEnd = computed(() => serverUsage.value?.period_end ?? null)
	const subscription = computed(() => serverUsage.value?.subscription ?? null)

	/**
	 * Несостоявшееся продление: списание по автопродлению не прошло, Pro-доступ закончился.
	 * Единственный статус подписки, на который смотрит логика приложения — по нему гейт
	 * запирает генерации, а App показывает шторку (см. useFeatureGate, CONTEXT.md).
	 */
	const isSuspended = computed(() => subscription.value?.status === SUBSCRIPTION_STATUS.suspended)

	/**
	 * Незавершённый заказ по данным сервера. Второй канал восстановления помимо
	 * localStorage: человек мог вернуться из другого браузера или почистить хранилище.
	 */
	const pendingOrder = computed(() => serverUsage.value?.pending_order ?? null)

	/**
	 * Единственный источник для UsageBar. Приоритет: сервер → гостевая cookie → нет данных.
	 * null означает «показывать нечего» — бар не рисуется.
	 */
	const items = computed<UsageItem[] | null>(() => {
		const server = serverUsage.value
		if (server) {
			return [
				{ key: 'text', value: server.limits.text.remaining, forms: PLURAL_FORMS.text },
				{ key: 'image', value: server.limits.image.remaining, forms: PLURAL_FORMS.image },
				{ key: 'song', value: server.limits.song.remaining, forms: PLURAL_FORMS.song },
				{ key: 'day', value: daysLeft(server.period_end), forms: PLURAL_FORMS.day }
			]
		}
		if (isGuest.value) {
			return [
				{ key: 'text', value: guestRemaining.value, forms: PLURAL_FORMS.text },
				{ key: 'image', value: 0, forms: PLURAL_FORMS.image },
				{ key: 'song', value: 0, forms: PLURAL_FORMS.song },
				{ key: 'day', value: 0, forms: PLURAL_FORMS.day }
			]
		}
		return null
	})

	/**
	 * Применяет серверный срез: из GET /usage, из SSE-события в конце стрима или из тела
	 * ответа генерации (три канала, §5.2.1). Сервер — истина, поэтому просто замена.
	 * Нераспознанную форму игнорируем: затереть срез мусором хуже, чем не обновить его.
	 * Возвращает true, если срез применён — по нему вызывающий решает, нужен ли фолбэк-запрос.
	 */
	function applyUsage(usage: RawUsage | null, { full = false } = {}): boolean {
		const next = normalizeUsage(usage)
		if (!next) return false
		// Срез стрима собирается вокруг лимитов: отсутствие ключа subscription в нём значит
		// «про подписку ничего не сказано», а не «подписки не стало». Затереть её здесь —
		// молча снять блокировку сорванного продления до следующего GET /usage, поэтому
		// прежнее значение переносим. Явный null — уже данные, его уважаем.
		//
		// Полный документ GET /usage (full) — другое дело: он авторитетен целиком, и если
		// подписки в нём нет, значит её действительно нет. Иначе восстановить доступ после
		// оплаты было бы нечем: блокировка залипла бы до перезагрузки страницы.
		const keepSubscription = !full && !('subscription' in next) && !!serverUsage.value?.subscription
		serverUsage.value = keepSubscription ? { ...next, subscription: serverUsage.value?.subscription } : next
		return true
	}

	/**
	 * Дотягивает срез. Ошибку глушим: без остатков интерфейс работает по fail-open,
	 * а запирать платящего юзера из-за сетевого сбоя нельзя (§5.2.1, крайние случаи).
	 * Гостя тоже спрашиваем: бэк отвечает ему plan: "guest" со своим остатком.
	 */
	async function fetchUsage() {
		try {
			// Конверт data (если он есть) снимает normalizeUsage — здесь про форму не знаем.
			applyUsage(await getUsage(), { full: true })
		} catch (error) {
			console.warn('usage не загрузился', error)
		}
	}

	/**
	 * Списание текстового запроса. При серверных лимитах фронт не считает — сервер истина.
	 */
	function consumeText() {
		if (serverUsage.value || !isGuest.value) return
		guestUsed.value = Math.min(GUEST_FREE_LIMIT, guestUsed.value + 1)
		writeGuestUsed(guestUsed.value)
	}

	/**
	 * Сервер ответил «лимит исчерпан». Синхронизируем локальный счётчик: cookie могли
	 * почистить или подделать, а сервер — истина. Нужно, чтобы UsageBar показал 0 —
	 * следующую попытку всё равно отклоняет сервер, фронт заранее не блокирует.
	 */
	function exhaustGuest() {
		if (!isGuest.value) return
		guestUsed.value = GUEST_FREE_LIMIT
		writeGuestUsed(GUEST_FREE_LIMIT)
	}

	/**
	 * Сервер ответил «лимит тарифа исчерпан» (402). Обнуляем остаток нужного ресурса,
	 * чтобы бар не показывал устаревшее число до следующего среза.
	 */
	function exhaustResource(resource: UsageResource) {
		const server = serverUsage.value
		if (!server) return
		const limit = server.limits[resource]
		serverUsage.value = {
			...server,
			limits: { ...server.limits, [resource]: { ...limit, used: limit.limit, remaining: 0 } }
		}
	}

	/**
	 * Локально правит срез подписки, чтобы карточка кабинета перерисовалась сразу после
	 * ответа сервера: ждать следующего GET /usage, чтобы поменять бейдж, нельзя.
	 * Нет среза — правку применять не к чему.
	 */
	function setAutoRenew(value: boolean) {
		const server = serverUsage.value
		if (!server) return
		// Сорванное продление локальной правкой не лечится: подменив статус на cancelled,
		// мы бы сняли блокировку генераций, которую держит isSuspended, — и запросы поехали
		// бы на бэк, который их всё равно отвергнет. Отключение автопродления поверх
		// suspended меняет только флаг.
		const status = isSuspended.value
			? server.subscription?.status
			: // cancelled = автопродления нет, но доступ живёт до current_period_end.
				value
				? SUBSCRIPTION_STATUS.active
				: SUBSCRIPTION_STATUS.cancelled
		serverUsage.value = {
			...server,
			subscription: {
				...server.subscription,
				is_renewable: value,
				status
			}
		}
	}

	/**
	 * Отключение автопродления. Локальное состояние меняем только после успеха сервера:
	 * экран про деньги не имеет права обещать то, чего не произошло.
	 *
	 * 409 AUTO_RENEW_ALREADY_OFF — тоже успех: автопродление уже выключено, а показывать
	 * ошибку в ответ на «отключите мне списания» абсурдно.
	 * Возвращает false, если отменить не удалось — вызывающий покажет это человеку.
	 */
	async function cancelAutoRenew(): Promise<boolean> {
		try {
			await postCancelSubscription()
		} catch (error) {
			const api = error instanceof ApiError ? error : null
			const alreadyOff = api?.code === BILLING_ERROR_CODES.AUTO_RENEW_ALREADY_OFF
			if (!alreadyOff) {
				console.warn('автопродление не отключилось', error)
				return false
			}
		}
		setAutoRenew(false)
		return true
	}

	function resetGuestUsed() {
		guestUsed.value = 0
		Cookies.remove(COOKIE_KEYS.GUEST_FREE_USED, { path: '/' })
	}

	return {
		serverUsage,
		guestUsed,
		isGuest,
		guestRemaining,
		plan,
		isPro,
		limits,
		periodEnd,
		subscription,
		isSuspended,
		pendingOrder,
		items,
		applyUsage,
		fetchUsage,
		consumeText,
		exhaustGuest,
		exhaustResource,
		setAutoRenew,
		cancelAutoRenew,
		resetGuestUsed
	}
})
